/**
 * The router's `rewrite` option (TanStack Router LocationRewrite): the route tree and every `to="/…"` link
 * keep the internal English paths, and only the address bar and the rendered hrefs carry the public,
 * language-prefixed paths of PDR-0019/PDR-0020 (see localised-path.ts).
 *
 * - input (address → router): `/de/produkte/ja-150m?x#y` → `/products/ja-150m?x#y`, and the URL language
 *   becomes `de`. A legacy slug or another moved form (product code in capitals, trailing slash) is read as
 *   the page it moved to; the router then replaces the address with the current one when it mounts (its
 *   Transitioner rebuilds the location and commits it with `replace` when the public href differs). In the
 *   browser the Koa server answers those with a 301 first, so this only matters for the dev server.
 *   A prefixed address that is not a page (`/cs/categories/x`, English words under a prefix) is kept as it
 *   is: it matches no route, so the router renders its not-found component; the language is still taken
 *   from the prefix. A prefix-less address is taken as an internal path, as before the language prefix.
 * - output (router → address/href): the internal path localised for the URL language. While no language
 *   is known (prefix-less addresses only) the path stays internal, so the dev server and the tests with
 *   memory history at internal paths behave as before. A path that already starts with a language prefix
 *   (the kept unknown address above) passes through unchanged.
 *
 * Search and hash are never touched: they stay on the URL object in both directions.
 */
import type { LocationRewrite } from '@tanstack/react-router';
import { type LanguageCode, languageCodes } from '../../texts/languages';
import { type LocalisedPaths, parseLocalisedPath, toLocalisedPath } from './localised-path';
import type { UrlLanguageStore } from './url-language';

/** The language named by the first segment of a pathname, if it names one. */
function prefixLanguage(pathname: string): LanguageCode | undefined {
  const first = pathname.split('/')[1];

  return (languageCodes as readonly string[]).includes(first) ? (first as LanguageCode) : undefined;
}

const defaultPaths: LocalisedPaths = { parseLocalisedPath, toLocalisedPath };

/** `paths` defaults to src/data/slugs.json; tests pass createLocalisedPaths over their own table. */
export function createLocalisedUrlRewrite(
  urlLanguage: UrlLanguageStore,
  paths: LocalisedPaths = defaultPaths,
): LocationRewrite {
  return {
    input: ({ url }) => {
      const parsed = paths.parseLocalisedPath(url.pathname);
      const page = parsed.kind === 'redirect' ? paths.parseLocalisedPath(parsed.location) : parsed;

      if (page.kind === 'page') {
        urlLanguage.set(page.lang);
        url.pathname = page.internalPath;

        return url;
      }
      const language = prefixLanguage(url.pathname);
      if (language) {
        urlLanguage.set(language);
      }

      return url;
    },
    output: ({ url }) => {
      const language = urlLanguage.current();
      if (language && !prefixLanguage(url.pathname)) {
        url.pathname = paths.toLocalisedPath(language, url.pathname);
      }

      return url;
    },
  };
}
